import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyV2Dto,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { WebhookDto, WebhookEventTypeDto } from '@fleet-beat/api';

@Injectable({
  providedIn: 'root',
})
export class WebhooksApiService {
  private readonly baseAdminUrl: string = `${this.environment.apiEndpoint}/admin/webhooks`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getManyAsAdmin(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<WebhookDto>> {
    return this.baseApiService.get(this.baseAdminUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<WebhookDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<WebhookDto[]>) => ({ data: response.data, ...response.meta })),
    );
  }

  postOneAsAdmin(data: WebhookDto, auth: AuthHeaders): Observable<WebhookDto> {
    return this.baseApiService.post(this.baseAdminUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<WebhookDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOneAsAdmin(id: string, data: WebhookDto, auth: AuthHeaders): Observable<WebhookDto> {
    return this.baseApiService.patch(`${this.baseAdminUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<WebhookDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  deleteAsAdmin(id: string, auth: AuthHeaders): Observable<WebhookDto> {
    return this.baseApiService.delete(`${this.baseAdminUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<WebhookDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getEventTypesAsAdmin(auth: AuthHeaders): Observable<WebhookEventTypeDto[]> {
    return this.baseApiService.get(`${this.baseAdminUrl}/event-types`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<WebhookEventTypeDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }
}
